import { Link } from "react-router-dom";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { FAQ_CATEGORIES, CONTACT_EMAIL, TRUST_ICON } from "@/data/content";
import { ArrowRight, Mail } from "lucide-react";

const Label = ({ children, className = "" }) => (
    <span className={`font-mono text-[11px] uppercase tracking-[0.24em] ${className}`}>
        {children}
    </span>
);

export default function FAQ() {
    const faqJsonLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_CATEGORIES.flatMap((c) =>
            c.items.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            }))
        ),
    };

    return (
        <div data-testid="faq-page" className="pt-[76px]">
            <Seo
                title="DentAssists FAQ — Dental Billing & Insurance Questions Answered"
                description="Answers to common questions about DentAssists for Texas and U.S. dental practices: HIPAA compliance and BAAs, supported practice management systems, pricing, onboarding time, and support."
                path="/faq"
                jsonLd={faqJsonLd}
            />
            <section className="relative overflow-hidden">
                <div className="glow-terracotta absolute -top-24 -right-24 w-[520px] h-[520px] pointer-events-none" />
                <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-12">
                    <Reveal>
                        <span className="inline-flex items-center gap-2 border border-linec bg-cream/70 backdrop-blur px-3 py-1.5 rounded-full">
                            <TRUST_ICON size={13} className="text-terracotta" />
                            <Label className="text-sage">Help Center</Label>
                        </span>
                        <h1 className="font-heading text-5xl sm:text-6xl tracking-tight text-ink mt-6 max-w-3xl leading-[1]">
                            Frequently asked questions.
                        </h1>
                        <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-2xl">
                            Answers about our dental billing, insurance verification, credentialing,
                            and reporting services — including HIPAA compliance and how we work inside
                            your existing software.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-20">
                <div className="grid lg:grid-cols-[0.32fr_0.68fr] gap-10 lg:gap-16">
                    {/* Sticky category index */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-28">
                            <Label className="text-terracotta">Categories</Label>
                            <ul className="mt-5 space-y-3">
                                {FAQ_CATEGORIES.map((c) => (
                                    <li key={c.category}>
                                        <a
                                            href={`#${slug(c.category)}`}
                                            data-testid={`faq-jump-${slug(c.category)}`}
                                            className="text-sm text-inksoft hover:text-sage transition-colors duration-200 border-l-2 border-linec hover:border-terracotta pl-4 block py-1"
                                        >
                                            {c.category}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </aside>

                    <div className="space-y-16">
                        {FAQ_CATEGORIES.map((cat, ci) => (
                            <div key={cat.category} id={slug(cat.category)} className="scroll-mt-28">
                                <Reveal>
                                    <div className="flex items-baseline gap-4 border-b border-linec pb-4">
                                        <span className="font-heading text-2xl text-terracotta/80">
                                            0{ci + 1}
                                        </span>
                                        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-ink">
                                            {cat.category}
                                        </h2>
                                    </div>
                                </Reveal>
                                <Reveal delay={0.06}>
                                    <Accordion
                                        type="single"
                                        collapsible
                                        className="mt-2"
                                        data-testid={`faq-accordion-${slug(cat.category)}`}
                                    >
                                        {cat.items.map((f, i) => (
                                            <AccordionItem
                                                key={f.q}
                                                value={`${ci}-${i}`}
                                                className="border-b border-linec"
                                            >
                                                <AccordionTrigger
                                                    data-testid={`faq-q-${ci}-${i}`}
                                                    className="font-heading text-lg sm:text-xl text-ink py-6 text-left hover:no-underline hover:text-sage"
                                                >
                                                    {f.q}
                                                </AccordionTrigger>
                                                <AccordionContent className="text-inksoft text-base leading-relaxed pb-6 max-w-2xl">
                                                    {f.a}
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Still have questions */}
            <section className="bg-sage text-cream relative z-10 overflow-hidden">
                <div className="glow-terracotta absolute -bottom-32 right-0 w-[520px] h-[520px] pointer-events-none opacity-60" />
                <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-20 text-center">
                    <Reveal>
                        <Label className="text-terracotta">Still have questions?</Label>
                        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight mt-4 max-w-2xl mx-auto">
                            Didn't find your answer here?
                        </h2>
                        <p className="text-cream/70 mt-5 max-w-lg mx-auto leading-relaxed">
                            Tell us what you're trying to solve and we'll walk you through it.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-9">
                            <Link
                                to="/contact"
                                data-testid="faq-cta-button"
                                className="inline-flex items-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-sm font-medium transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5"
                            >
                                Get a Free Consultation
                                <ArrowRight size={18} />
                            </Link>
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                data-testid="faq-email-link"
                                className="inline-flex items-center gap-2 text-cream/90 border border-cream/25 px-8 py-4 rounded-sm font-medium transition-colors duration-200 hover:bg-cream/10"
                            >
                                <Mail size={16} />
                                {CONTACT_EMAIL}
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}

function slug(s) {
    return s
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}
